import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  getAuthenticatedLogoUrl,
  getOrgBranding,
  ORG_BRANDING_CHANGED_EVENT,
  type OrgSettings,
} from '../api/org'
import { useAuth } from '../hooks/useAuth'
import { orgPath, resolveOrgSlug, SUPERADMIN_DEFAULT_ORG_SLUG } from '../utils/org-slug'
import AppEventListener from './AppEventListener'
import BrandLogo from './BrandLogo'
import OrgSwitcherModal from './OrgSwitcherModal'
import { SETUP_NAV_ITEMS } from './setup/setupNav'
import ThemeToggle from './ThemeToggle'

const coreNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: '📊' },
  { to: '/projects', label: 'Projects', icon: '📁' },
  { to: '/search', label: 'Search', icon: '🔍' },
  { to: '/memory', label: 'Memory', icon: '🧠' },
  { to: '/prompts', label: 'Prompts', icon: '💬' },
]

const SETUP_ICONS: Record<string, string> = {
  '/setup/mcp': '⚙️',
  '/setup/project': '📝',
}

const resourceNavItems = [{ to: '/docs', label: 'API Docs', icon: '📖', external: true }]

const adminNavItems = [
  { to: '/admin/org', label: 'Organization', icon: '🏢' },
  { to: '/admin/users', label: 'Users', icon: '👥' },
  { to: '/admin/access', label: 'Project Access', icon: '🔐' },
  { to: '/admin/settings', label: 'System Settings', icon: '🔧' },
  { to: '/admin/observability', label: 'Observability', icon: '📈' },
  { to: '/admin/api-key', label: 'Admin API Key', icon: '🔑' },
]

const superAdminNavItems = [
  { to: '/admin/orgs', label: 'Organizations', icon: '🏢' },
  { to: '/admin/platform', label: 'Platform', icon: '🌐' },
  { to: '/admin/provider-templates', label: 'Provider Templates', icon: '🔌' },
  { to: '/admin/builtin-prompts', label: 'Built-in Prompts', icon: '💬' },
]

/**
 * Org branding below ContextHarbor in the sidebar header: logo + name when a
 * logo is set, name alone otherwise. A stale logoUrl (file removed on disk,
 * expired token) hides the <img> via onError so a broken-image icon never
 * renders. `key`d by logoUrl so a new upload/removal resets the error state.
 */
function OrgBrand({
  org,
  logoSrc,
  onSwitch,
}: {
  org: OrgSettings
  logoSrc: string | null
  onSwitch: () => void
}) {
  const [logoBroken, setLogoBroken] = useState(false)
  const showLogo = Boolean(logoSrc) && !logoBroken
  return (
    <button
      type="button"
      onClick={onSwitch}
      title={`Switch organization (current: ${org.name})`}
      aria-label={`Switch organization, current organization ${org.name}`}
      className="flex w-full items-center gap-2.5 pt-3 mt-1 border-t border-harbor-border text-left rounded-lg hover:bg-harbor-surface-raised px-1 py-1 transition-colors"
    >
      {showLogo && (
        <img
          src={logoSrc ?? undefined}
          alt={`${org.name} logo`}
          width={28}
          height={28}
          onError={() => setLogoBroken(true)}
          className="rounded-lg object-cover shrink-0 overflow-hidden"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-harbor-text truncate">{org.name}</p>
        {showLogo ? (
          <p className="text-xs text-harbor-text-secondary font-mono truncate">/{org.slug}</p>
        ) : (
          <p className="text-xs text-harbor-text-secondary truncate">Switch organization</p>
        )}
      </div>
      <span aria-hidden="true" className="text-harbor-text-muted text-xs shrink-0">
        ⇅
      </span>
    </button>
  )
}

export default function Layout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const onSetupRoute = location.pathname.startsWith('/setup/')
  const [setupOpen, setSetupOpen] = useState(onSetupRoute)
  const [setupOpenManual, setSetupOpenManual] = useState<boolean | null>(null)
  const [org, setOrg] = useState<OrgSettings | null>(null)
  const [switcherOpen, setSwitcherOpen] = useState(false)

  // Current org slug: prefer the loaded branding (covers renamed slugs), else
  // fall back to the URL's first segment. Used to gate the superadmin-only
  // Organizations nav item to a single org.
  const orgSlug = org?.slug ?? resolveOrgSlug()

  // Auto-expand "Setup Instructions" whenever the user lands on a setup page,
  // unless they explicitly collapsed it afterwards (manual override wins).
  useEffect(() => {
    if (setupOpenManual !== null) {
      setSetupOpen(setupOpenManual)
      return
    }
    if (onSetupRoute) setSetupOpen(true)
  }, [onSetupRoute, setupOpenManual])

  const toggleSetup = () => {
    const next = !setupOpen
    setSetupOpen(next)
    setSetupOpenManual(next)
  }

  const setupActive = SETUP_NAV_ITEMS.some((item) => location.pathname.startsWith(item.to))

  // Org branding for the sidebar header (logo + name below ContextHarbor).
  // Also carries restApiEnabled, which hides the API Docs sidebar link when
  // direct REST API access is disabled. Refreshes on ORG_BRANDING_CHANGED_EVENT
  // (fired by Org settings saves and the REST API toggle) so the sidebar
  // updates without a page reload. Defaults to visible when branding fails
  // to load (fail-open, e.g. non-admins on older servers).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const branding = await getOrgBranding()
        if (!cancelled) setOrg(branding)
      } catch {
        if (!cancelled) setOrg(null)
      }
    }
    void load()
    const onBrandingChanged = () => void load()
    window.addEventListener(ORG_BRANDING_CHANGED_EVENT, onBrandingChanged)
    return () => {
      cancelled = true
      window.removeEventListener(ORG_BRANDING_CHANGED_EVENT, onBrandingChanged)
    }
  }, [])

  const showApiDocs = org?.restApiEnabled ?? true
  const visibleResourceNavItems = showApiDocs ? resourceNavItems : []

  const orgLogoSrc = getAuthenticatedLogoUrl(org?.logoUrl)

  const handleLogout = () => {
    logout()
    // Auth pages live only at bare URLs: break out of the org-scoped router
    // basename with a hard navigation to /login (SPA navigate would keep the
    // /:orgSlug prefix and land on /:orgSlug/login).
    window.location.href = '/login'
  }

  return (
    <div className="min-h-screen app-sky text-harbor-text">
      <AppEventListener />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-harbor-surface/80 backdrop-blur-md border-r border-harbor-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-5 border-b border-harbor-border">
            <div className="flex items-center gap-3">
              <BrandLogo size={40} className="rounded-lg" />
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-harbor-text">ContextHarbor</h1>
                <p className="text-xs text-harbor-text-secondary">Portal</p>
              </div>
            </div>
            {org && (
              <OrgBrand
                key={org.logoUrl ?? 'no-logo'}
                org={org}
                logoSrc={orgLogoSrc}
                onSwitch={() => setSwitcherOpen(true)}
              />
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {coreNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={orgPath(item.to)}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-600/20 text-emerald-400'
                      : 'text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text'
                  }`
                }
              >
                <span className="text-lg" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
              </NavLink>
            ))}

            {/* Setup Instructions — collapsible group for all setup guides */}
            <div>
              <button
                type="button"
                onClick={toggleSetup}
                aria-expanded={setupOpen}
                aria-controls="setup-instructions-nav"
                className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  setupActive
                    ? 'bg-emerald-600/20 text-emerald-400'
                    : 'text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text'
                }`}
              >
                <span className="text-lg" aria-hidden="true">
                  🧭
                </span>
                Setup Instructions
                <span
                  aria-hidden="true"
                  className={`ml-auto text-xs transition-transform duration-200 ${setupOpen ? 'rotate-90' : ''}`}
                >
                  ▶
                </span>
              </button>
              {setupOpen && (
                <ul
                  id="setup-instructions-nav"
                  className="mt-1 ml-4 space-y-1 border-l border-harbor-border pl-2"
                >
                  {SETUP_NAV_ITEMS.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={orgPath(item.to)}
                        onClick={() => setSidebarOpen(false)}
                        title={item.blurb}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-emerald-600/20 text-emerald-400'
                              : 'text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text'
                          }`
                        }
                      >
                        <span className="text-base" aria-hidden="true">
                          {SETUP_ICONS[item.to] ?? '•'}
                        </span>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {visibleResourceNavItems.map((item) => (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text transition-colors"
              >
                <span className="text-lg" aria-hidden="true">
                  {item.icon}
                </span>
                {item.label}
                <span className="ml-auto text-xs text-harbor-text-muted">↗</span>
              </a>
            ))}

            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <>
                <div className="pt-4 pb-2 px-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-harbor-text-muted">
                    Admin
                  </p>
                </div>
                {adminNavItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={orgPath(item.to)}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-emerald-600/20 text-emerald-400'
                          : 'text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text'
                      }`
                    }
                  >
                    <span className="text-lg" aria-hidden="true">
                      {item.icon}
                    </span>
                    {item.label}
                  </NavLink>
                ))}
                {user?.role === 'superadmin' &&
                  orgSlug === SUPERADMIN_DEFAULT_ORG_SLUG &&
                  superAdminNavItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={orgPath(item.to)}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : 'text-harbor-text-secondary hover:bg-harbor-surface-raised hover:text-harbor-text'
                        }`
                      }
                    >
                      <span className="text-lg" aria-hidden="true">
                        {item.icon}
                      </span>
                      {item.label}
                    </NavLink>
                  ))}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-harbor-border">
            <ThemeToggle />
            <Link
              to="/profile"
              className="flex items-center gap-3 mt-3 mb-3 rounded-lg hover:bg-harbor-surface-raised px-1 py-1 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-harbor-surface-raised flex items-center justify-center text-sm font-medium text-harbor-text">
                {user?.username?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-harbor-text truncate">{user?.username}</p>
                <p className="text-xs text-harbor-text-secondary truncate">{user?.email}</p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full px-3 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-harbor-surface/70 backdrop-blur-md border-b border-harbor-border">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-harbor-text-secondary hover:text-harbor-text rounded-lg hover:bg-harbor-surface-raised"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="flex-1" />
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <OrgSwitcherModal
        isOpen={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        currentSlug={org?.slug}
      />
    </div>
  )
}
